import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useTestContext } from '../../context/testContext';
import { helpBoxContent } from '../../helpers/helpBoxContent';
import { IconButton, CloseIcon, InfoIcon, LocalVideoTile, RemoteVideoTile, useSessionChrome, useLayoutMode, colors } from '../../ui';
import { useSessionPeople } from '../session/hooks';
import CompletedExaminations from '../session/CompletedExaminations';

import {
  Tests,
  Calibration,
  SetDistance,
  AcuityResults,
  ICNVisionResults,
  ContrastSensitivityResults,
  RefractionResults,
  VisualFieldResults,
  NeuroSuiteResults,
  ColourVisionResults,
} from './';

// Content of the clinician main panel. The panel itself, the header title and the nav row are
// provided by ClinicianSessionFrame / SessionLayout (src/ui); setTitle writes the header title.

const HelpToggle = styled.div`
  position: absolute;
  top: 12px;
  right: 48px;
  z-index: 4;
`;

const HelpOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 32px 32px;
  background: #fff;
  overflow: auto;
`;

const Consultation = styled.div`
  flex: 1;
  display: grid;
  gap: 16px;
  padding: 16px;
  min-height: 0;
  grid-template-columns: ${({ mode }) => (mode === 'landscape' ? '2fr 1fr' : '1fr')};
  grid-template-rows: ${({ mode }) => (mode === 'landscape' ? '1fr' : '2fr 1fr')};
  background: ${colors.slate[50]};
`;

const ConsultationView = () => {
  const people = useSessionPeople();
  const mode = useLayoutMode();
  return (
    <Consultation mode={mode} data-testid='consultation-view'>
      <RemoteVideoTile roleLabel={people.remoteLabel} name={people.remoteName} />
      <LocalVideoTile roleLabel={people.localLabel} name={people.localName} showMenu />
    </Consultation>
  );
};

const HelpButton = () => {
  const [open, setOpen] = useState(false);
  const {
    testData: { testKey },
  } = useTestContext();
  const content = testKey?.endsWith('Logmar') ? helpBoxContent['logmar'] : helpBoxContent[testKey];
  if (!content) return null;
  return (
    <>
      <HelpToggle>
        <IconButton label={open ? 'Close help' : 'How this test works'} variant={open ? 'outline' : 'teal'} round size={28} onClick={() => setOpen((o) => !o)}>
          {open ? <CloseIcon size={14} /> : <InfoIcon size={16} />}
        </IconButton>
      </HelpToggle>
      {open && <HelpOverlay data-testid='test-help'>{content}</HelpOverlay>}
    </>
  );
};

export const MainPanelContent = ({ calibrationStep, setCalibrationStep, activeTab, setActiveTab, response, setResponse }) => {
  const { setTitle } = useSessionChrome();
  const {
    testData: { testKey },
  } = useTestContext();

  const contentOptions = [
    {
      id: 'calibration-selection',
      content: (
        <Calibration
          setTitle={setTitle}
          setActiveTab={setActiveTab}
          response={response}
          setResponse={setResponse}
          calibrationStep={calibrationStep}
          setCalibrationStep={setCalibrationStep}
        />
      ),
    },
    { id: 'consultationView', content: <ConsultationView /> },
    { id: 'set-distance', content: <SetDistance setTitle={setTitle} setActiveTab={setActiveTab} /> },
    { id: 'results', content: <CompletedExaminations setActiveTab={setActiveTab} setTitle={setTitle} /> },
    { id: 'acuityResults', content: <AcuityResults setActiveTab={setActiveTab} /> },
    { id: 'icnResults', content: <ICNVisionResults setActiveTab={setActiveTab} /> },
    { id: 'contrastSensitivityResults', content: <ContrastSensitivityResults setActiveTab={setActiveTab} /> },
    { id: 'visualFieldResults', content: <VisualFieldResults setActiveTab={setActiveTab} /> },
    { id: 'refractionResults', content: <RefractionResults setActiveTab={setActiveTab} /> },
    { id: 'neuroSuiteResults', content: <NeuroSuiteResults setActiveTab={setActiveTab} /> },
    { id: 'colourVisionResults', content: <ColourVisionResults setActiveTab={setActiveTab} /> },
    { id: 'test', content: <Tests setActiveTab={setActiveTab} setTitle={setTitle} /> },
  ];

  const selectedContent = contentOptions.find((selectedTab) => activeTab === selectedTab.id);
  useEffect(() => {
    if (activeTab === 'consultationView') setTitle('Consultation View');
  }, [activeTab, setTitle]);

  return (
    <>
      {activeTab === 'test' && testKey && <HelpButton />}
      {selectedContent?.content || null}
    </>
  );
};
